import React, { useEffect, useState } from 'react';
import { getAdminSettings, updateAdminSettings, getAdminLevels, createLevel, updateLevel, deleteLevel } from '../../services/api';
import { Loader2, Plus, Trash2, Pencil, Eye, EyeOff, X, Save, Settings, Layers, Phone } from 'lucide-react';
import MediaUploader from './MediaUploader';

// Normalize a mixed-format Instagram tile item into {url, link, kind}
const normalizeIgTile = (item) => {
  if (!item) return { url: '', link: '', kind: 'image' };
  if (typeof item === 'string') return { url: item, link: '', kind: 'image' };
  return {
    url: item.url || '',
    link: item.link || '',
    kind: item.kind || (item.url && /\.(mp4|webm|mov)(\?|$)/i.test(item.url) ? 'video' : 'image'),
  };
};

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('sections');
  const [levelModal, setLevelModal] = useState(null);

  const fetchAll = async () => {
    try {
      const [sRes, lRes] = await Promise.all([getAdminSettings(), getAdminLevels()]);
      setSettings(sRes.data);
      setLevels(lRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await updateAdminSettings(settings);
      alert('Ayarlar kaydedildi!');
    } catch (err) { alert('Hata oluştu'); }
    finally { setSaving(false); }
  };

  const toggleLevelActive = async (lvl) => {
    await updateLevel(lvl.id, { active: !lvl.active });
    fetchAll();
  };

  const handleDeleteLevel = async (lvl) => {
    if (!window.confirm(`"${lvl.name}" silinecek?`)) return;
    await deleteLevel(lvl.id);
    fetchAll();
  };

  const handleSaveLevel = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd.entries());
    data.level = Number(data.level) || 1;
    data.order = Number(data.order) || 1;
    data.active = true;
    try {
      if (levelModal.mode === 'create') {
        await createLevel(data);
      } else {
        await updateLevel(levelModal.data.id, data);
      }
      setLevelModal(null);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Hata'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3D4A26]" size={32} /></div>;

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {[{key:'sections',label:'Bölüm Ayarları',icon:Settings},{key:'contact',label:'İletişim',icon:Phone},{key:'levels',label:'Seviyeler',icon:Layers},{key:'policy',label:'İptal Politikası',icon:Settings}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-[#3D4A26] text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}>
            <t.icon size={16} />{t.label}
          </button>
        ))}
      </div>

      {/* Section Visibility */}
      {tab === 'sections' && settings && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl">
          <h3 className="font-bold text-[#1B1B1B] mb-4" style={{fontFamily:'DM Sans, sans-serif'}}>Ana Sayfa Bölümleri</h3>
          <p className="text-sm text-gray-500 mb-6">Hangi bölümlerin ana sayfada görüneceğini belirleyin.</p>
          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50">
              <div><p className="font-semibold text-sm">Seviyeler Bölümü</p><p className="text-xs text-gray-500">&ldquo;Farklı Seviyeler, Hepsi Epik Maceralar&rdquo;</p></div>
              <input type="checkbox" checked={settings.levelsVisible || false}
                onChange={e => setSettings({...settings, levelsVisible: e.target.checked})}
                className="w-5 h-5 accent-[#C8D843]" />
            </label>
            <label className="flex items-center justify-between p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50">
              <div><p className="font-semibold text-sm">Blog / İlham Bölümü</p><p className="text-xs text-gray-500">Dergi/blog kartları bölümü</p></div>
              <input type="checkbox" checked={settings.blogVisible !== false}
                onChange={e => setSettings({...settings, blogVisible: e.target.checked})}
                className="w-5 h-5 accent-[#C8D843]" />
            </label>
          </div>
          <button onClick={saveSettings} disabled={saving}
            className="mt-6 flex items-center gap-2 bg-[#3D4A26] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Kaydet
          </button>
        </div>
      )}

      {/* Contact Info */}
      {tab === 'contact' && settings && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-2xl">
          <h3 className="font-bold text-[#1B1B1B] mb-2" style={{fontFamily:'DM Sans, sans-serif'}}>İletişim Bilgileri</h3>
          <p className="text-sm text-gray-500 mb-6">Footer, Bize Ulaşın ve KVKK sayfalarında gösterilen bilgiler.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1">E-posta</label>
              <input
                data-testid="contact-email-input"
                value={settings.email || ''}
                onChange={e => setSettings({...settings, email: e.target.value})}
                placeholder="hello@camption.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Telefon</label>
              <input
                value={settings.phone || ''}
                onChange={e => setSettings({...settings, phone: e.target.value})}
                placeholder="0 (850) 495 05 24"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">WhatsApp (sadece rakam, ülke kodlu)</label>
              <input
                value={settings.whatsapp || ''}
                onChange={e => setSettings({...settings, whatsapp: e.target.value})}
                placeholder="908504950524"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1">Adres</label>
              <input
                value={settings.address || ''}
                onChange={e => setSettings({...settings, address: e.target.value})}
                placeholder="Antalya, Türkiye"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]"
              />
            </div>
          </div>

          <h4 className="text-sm font-bold text-[#1B1B1B] mt-7 mb-3">Mesai Saatleri</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1">Hafta içi</label>
              <input value={settings.hoursWeekday || ''} onChange={e => setSettings({...settings, hoursWeekday: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Cumartesi</label>
              <input value={settings.hoursSaturday || ''} onChange={e => setSettings({...settings, hoursSaturday: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Pazar</label>
              <input value={settings.hoursSunday || ''} onChange={e => setSettings({...settings, hoursSunday: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
            </div>
          </div>

          <h4 className="text-sm font-bold text-[#1B1B1B] mt-7 mb-3">Sosyal Medya (tam URL)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1">Instagram</label>
              <input value={settings.instagram || ''} onChange={e => setSettings({...settings, instagram: e.target.value})} placeholder="https://instagram.com/camption" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Twitter / X</label>
              <input value={settings.twitter || ''} onChange={e => setSettings({...settings, twitter: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Facebook</label>
              <input value={settings.facebook || ''} onChange={e => setSettings({...settings, facebook: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">YouTube</label>
              <input value={settings.youtube || ''} onChange={e => setSettings({...settings, youtube: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" />
            </div>
          </div>

          <h4 className="text-sm font-bold text-[#1B1B1B] mt-7 mb-2">Instagram Gönderileri</h4>
          <p className="text-xs text-gray-500 mb-4">
            Anasayfadaki Instagram bölümünde gösterilecek 6 gönderi. Her tile için görsel VEYA kısa video (MP4/WEBM/MOV, max 25MB) yükleyebilir; opsiyonel olarak o gönderiye/reels&apos;e yönlendirme linki verebilirsiniz. Boş bırakılanlar otomatik gizlenir.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[0,1,2,3,4,5].map((i) => {
              const posts = settings.instagramPosts || [];
              const tile = normalizeIgTile(posts[i]);
              const updateTile = (patch) => {
                const next = (settings.instagramPosts || []).map((p, idx) => (idx === i ? { ...normalizeIgTile(p), ...patch } : p));
                while (next.length < 6) next.push('');
                next[i] = { ...normalizeIgTile(posts[i]), ...patch };
                setSettings({ ...settings, instagramPosts: next });
              };
              const clearTile = () => {
                const next = [...(settings.instagramPosts || [])];
                while (next.length < 6) next.push('');
                next[i] = { url: '', link: '', kind: 'image' };
                setSettings({ ...settings, instagramPosts: next });
              };
              return (
                <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Gönderi {i + 1}</p>
                    {tile.url && (
                      <button
                        type="button"
                        onClick={clearTile}
                        data-testid={`ig-post-clear-${i}`}
                        className="text-[10px] text-red-500 hover:underline"
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                  {tile.url && (
                    <div className="relative w-full aspect-square rounded overflow-hidden bg-gray-100 mb-2">
                      {tile.kind === 'video' ? (
                        <video
                          src={tile.url}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          autoPlay
                          loop
                        />
                      ) : (
                        <img src={tile.url} alt="" className="w-full h-full object-cover" />
                      )}
                      <span className="absolute top-1 right-1 bg-white/90 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                        {tile.kind === 'video' ? 'Video' : 'Görsel'}
                      </span>
                    </div>
                  )}
                  <div className="space-y-2">
                    <MediaUploader
                      category="gallery"
                      label={tile.url ? 'Değiştir' : 'Yükle'}
                      onUpload={({ url, kind }) => updateTile({ url, kind })}
                    />
                    <input
                      value={tile.link || ''}
                      onChange={(e) => updateTile({ link: e.target.value })}
                      placeholder="Link (opsiyonel — reel/post URL)"
                      data-testid={`ig-post-link-${i}`}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#C8D843] bg-white"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <button onClick={saveSettings} disabled={saving}
            data-testid="save-contact-button"
            className="mt-6 flex items-center gap-2 bg-[#3D4A26] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Kaydet
          </button>
        </div>
      )}

      {/* Levels Management */}
      {tab === 'levels' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setLevelModal({mode:'create',data:{level:levels.length+1,order:levels.length+1}})}
              className="flex items-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-semibold px-4 py-2 rounded-lg hover:bg-[#b8c83a] text-sm">
              <Plus size={16} /> Yeni Seviye
            </button>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Seviye</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ad</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Açıklama</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Durum</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {levels.map(lvl => (
                  <tr key={lvl.id} className={`hover:bg-gray-50 ${!lvl.active ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-3"><span className="w-8 h-8 bg-[#C8D843] rounded-full flex items-center justify-center text-sm font-bold">{lvl.level}</span></td>
                    <td className="px-5 py-3 font-semibold text-sm">{lvl.name}</td>
                    <td className="px-5 py-3 text-sm text-gray-600 max-w-xs truncate">{lvl.description}</td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => toggleLevelActive(lvl)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${lvl.active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {lvl.active ? 'Aktif' : 'Gizli'}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setLevelModal({mode:'edit',data:lvl})} className="p-1.5 hover:bg-gray-100 rounded-lg"><Pencil size={16} className="text-gray-400" /></button>
                        <button onClick={() => handleDeleteLevel(lvl)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 size={16} className="text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Level Modal */}
          {levelModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setLevelModal(null)}>
              <form onSubmit={handleSaveLevel} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-bold text-lg">{levelModal.mode === 'create' ? 'Yeni Seviye' : 'Seviye Düzenle'}</h3>
                  <button type="button" onClick={() => setLevelModal(null)} className="p-1 hover:bg-gray-100 rounded-full"><X size={18} /></button>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-xs font-semibold mb-1">Seviye No *</label><input name="level" type="number" defaultValue={levelModal.data?.level || 1} required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                    <div><label className="block text-xs font-semibold mb-1">Sıra</label><input name="order" type="number" defaultValue={levelModal.data?.order || 1} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                  </div>
                  <div><label className="block text-xs font-semibold mb-1">Ad *</label><input name="name" defaultValue={levelModal.data?.name || ''} required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                  <div><label className="block text-xs font-semibold mb-1">Açıklama</label><textarea name="description" defaultValue={levelModal.data?.description || ''} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none" /></div>
                  <div><label className="block text-xs font-semibold mb-1">Görsel URL</label><input name="image" defaultValue={levelModal.data?.image || ''} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]" /></div>
                </div>
                <button type="submit" className="w-full mt-5 bg-[#3D4A26] text-white font-bold py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors">Kaydet</button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Cancellation Policy */}
      {tab === 'policy' && settings && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-2xl">
          <h3 className="font-bold text-[#1B1B1B] mb-2" style={{fontFamily:'DM Sans, sans-serif'}}>İptal Politikası</h3>
          <p className="text-sm text-gray-500 mb-4">Tüm tur detay sayfalarında gösterilecek iptal politikası.</p>
          <textarea
            value={settings.cancellationPolicy || ''}
            onChange={e => setSettings({...settings, cancellationPolicy: e.target.value})}
            rows={10}
            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] resize-none leading-relaxed"
            placeholder="İptal politikası metnini buraya yazın..."
          />
          <button onClick={saveSettings} disabled={saving}
            className="mt-4 flex items-center gap-2 bg-[#3D4A26] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Kaydet
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
